import axios, { AxiosInstance } from "axios";

import {
  ajaxHeaders,
  axiosInstances,
  getAjaxHeader,
  setAjaxHeader,
} from "./helpers/granularAxios.helpers";
import { SupportedLocale } from "../models";

export { getIsAxiosError } from "./helpers/granularAxios.helpers";

export const bootstrapAxiosInstance = <T extends AxiosInstance>(
  axiosInstance: T,
): T => {
  if ("Axios" in axiosInstance) {
    throw new Error("You may not bootstrap global axios instance");
  }
  if (axiosInstances.includes(axiosInstance)) {
    return axiosInstance;
  }
  // store axios instance
  axiosInstances.push(axiosInstance);
  // set initial default headers on axios instance
  Object.keys(ajaxHeaders).forEach((key) => {
    const value = ajaxHeaders[key];
    if (!axiosInstance.defaults.headers) {
      throw new Error("axios instance must have defaults.headers");
    }
    axiosInstance.defaults.headers.common[key] = value;
  });
  // throw SyntaxError if JSON parsing failed
  axiosInstance.interceptors.request.use((config) => ({
    ...config,
    transitional: {
      ...config.transitional,
      silentJSONParsing: false,
    },
  }));
  // axios instance is returned as a convenience
  return axiosInstance;
};

export const setAuthInfoHeader = (auth: {
  identityToken: string | undefined;
}): void => {
  setAjaxHeader(
    "Authorization",
    auth.identityToken === undefined
      ? undefined
      : `Bearer ${auth.identityToken}`,
  );
};
export const setUserHeader = (auth: {
  granularId: string | undefined;
}): void => {
  setAjaxHeader("x-granular-id", auth.granularId);
};
export const setOperationHeader = (operation: {
  id: string | undefined;
}): void => {
  setAjaxHeader("x-operation", operation.id);
};
export const setLocaleHeader = (locale: SupportedLocale | undefined): void => {
  setAjaxHeader("X-Granular-Locale", locale);
};

export const getAuthInfoHeader = (): string | undefined => {
  return getAjaxHeader("Authorization");
};
export const getUserHeader = (): string | undefined => {
  return getAjaxHeader("x-granular-id");
};
export const getOperationHeader = (): string | undefined => {
  return getAjaxHeader("x-operation");
};
export const getLocaleHeader = (): SupportedLocale | undefined => {
  return getAjaxHeader("X-Granular-Locale") as SupportedLocale | undefined;
};

export const authTokenAccessError = new Error(
  "Attempted to access authToken but it is not available",
);

export const getAuthToken = (): string => {
  const authToken = getAjaxHeader("Authorization")?.slice("Bearer ".length);
  if (!authToken) {
    throw authTokenAccessError;
  }
  return authToken;
};

export const granularAxiosInstance = bootstrapAxiosInstance(axios.create());
